import { useState } from "react";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ArrowRight, Check } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailModal({ open, onOpenChange }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    try {
      const { data } = await axios.post(`${API}/waitlist`, { email });
      setStatus("success");
      setMessage(data.message);
      setEmail("");
    } catch (err) {
      setStatus("error");
      const detail = err.response?.data?.detail;
      setMessage(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    }
  };

  const handleClose = (val) => {
    onOpenChange(val);
    if (!val) {
      // Reset state when closing
      setTimeout(() => {
        setStatus("idle");
        setMessage("");
        setEmail("");
      }, 300);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="email-modal"
        className="sm:max-w-md"
        style={{
          backgroundColor: "var(--blush)",
          border: "none",
          borderRadius: "20px",
          padding: "2rem",
        }}
      >
        <DialogHeader className="text-center sm:text-center">
          <DialogTitle
            data-testid="email-modal-title"
            style={{
              fontFamily: "var(--font-serif)",
              fontSize: "1.75rem",
              fontWeight: 600,
              color: "var(--charcoal)",
            }}
          >
            Get early access
          </DialogTitle>
          <DialogDescription
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: "0.95rem",
              color: "var(--charcoal-light)",
              lineHeight: 1.6,
              marginTop: "0.5rem",
            }}
          >
            Join the waitlist for Resonance and be the first to know when we launch.
          </DialogDescription>
        </DialogHeader>

        {status === "success" ? (
          <div
            data-testid="email-modal-success"
            className="flex items-center justify-center gap-3 py-6"
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: "1rem",
              color: "var(--coral)",
              fontWeight: 500,
            }}
          >
            <Check size={20} strokeWidth={2.5} />
            <span>{message}</span>
          </div>
        ) : (
          <form
            data-testid="email-modal-form"
            onSubmit={handleSubmit}
            className="flex flex-col gap-3 mt-4"
          >
            <Input
              data-testid="email-modal-input"
              type="email"
              placeholder="Your email address"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (status === "error") setStatus("idle");
              }}
              required
              className="h-12 px-4 rounded-full text-base"
              style={{
                fontFamily: "var(--font-sans)",
                backgroundColor: "var(--white)",
                border: "1px solid rgba(44,44,42,0.1)",
                borderRadius: "50px",
              }}
            />
            <button
              data-testid="email-modal-submit"
              type="submit"
              disabled={status === "loading"}
              className="btn-coral flex items-center justify-center gap-2 w-full"
              style={{
                padding: "14px 28px",
                fontSize: "0.95rem",
                opacity: status === "loading" ? 0.7 : 1,
              }}
            >
              {status === "loading" ? "Joining..." : "Join the waitlist"}
              {status !== "loading" && <ArrowRight size={16} />}
            </button>

            {status === "error" && (
              <p
                data-testid="email-modal-error"
                className="text-center"
                style={{
                  fontFamily: "var(--font-sans)",
                  fontSize: "0.875rem",
                  color: "#c53030",
                }}
              >
                {message}
              </p>
            )}
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
